// @ts-nocheck
import { useEffect, useCallback, useRef, useContext } from 'react'
import { useRouter } from 'next/router'
import axios from 'axios'
import { logger } from '@dmi-mch/utils'
import { Artworks as ArtworksService } from '@services'

import { useModuleContext } from './Context'
import useSortOptions from './useCatalogSortBy'
import { PageContext } from '@providers/PageContext'

const CancelToken = axios.CancelToken

/**
 *
 * @returns This hook should only be called from just one file, otherwise, API will trigger 2 times!
 */
const useData = () => {
  const { page: { pageData: pageMetaInfo } } = useContext(PageContext)
  const { id, displayName, cleanTitle } = pageMetaInfo
  const router = useRouter()
  const {
    state: { catalogArtworksList },
    dispatch,
    refs
  } = useModuleContext()
  const { currentOrderBy } = useSortOptions()

  const source = useRef<any>(null)
  const pageToken = useRef<any>(null)

  // Setting a type here is tough, because this can be an Event, an Artwork, Special Room, room, etc.
  const listPrevPage = useRef<any>(null)
  const pageSize = 36
  const page = useRef(1)
  // Pagination stuff

  // Main Events fetch
  const fetch = useCallback(async () => {
    if (source.current) {
      source.current.cancel()
    }
    const offset = pageSize * (page.current - 1)
    const [sortBy, direction, groupBy] = currentOrderBy.split('|')
    const descending = direction === 'desc'
    try {
      const params = {
        limit: 36,
        offset: offset,
        sortBy,
        descending,
        pageToken: pageToken.current,
        groupBy
      }

      source.current = CancelToken.source()

      const result = await ArtworksService.getArtworks({ galleryId: id, ...params })
      const newList = result.data.items?.length > 0 ? result.data.items : []
      pageToken.current = result?.data?.pageToken

      let listCopy = newList ? [...newList] : []
      if (page.current > 1) {
        listCopy = [...listPrevPage.current, ...newList]
      }
      refs.paginationInfo.current = {
        page: result.data.page,
        total: result.data.total,
        hasMore: result.data.hasMore
      }

      dispatch({ type: 'SET_IS_LOADING_LIST', payload: false })
      dispatch({ type: 'SET_IS_LOADING_MORE_ITEM', payload: false })
      dispatch({ type: 'SET_GALLERY_PROFILE_DATA', payload: { id, displayName, cleanTitle } })
      dispatch({
        type: 'SET_CATALOG_ARTWORKS_LIST',
        payload: listCopy
      })
    } catch (e) {
      logger(e)
    }
    source.current = null
  }, [cleanTitle, currentOrderBy, dispatch, displayName, id, refs.paginationInfo])

  // Saving a copy of events every time. Will eventually be appended in case of pagination
  useEffect(() => {
    listPrevPage.current = catalogArtworksList || []
  }, [catalogArtworksList])

  // This is what triggers the first fecth.
  // On every fetch caused by a change of route (which means a change of filters), set the page back to 1.
  useEffect(() => {
    dispatch({ type: 'SET_IS_LOADING_LIST', payload: true })
    page.current = 1
    fetch()
  }, [router, fetch, dispatch])

  // Request new page, by increasing page by 1.
  const loadNextPage = useCallback(() => {
    dispatch({
      type: 'SET_IS_LOADING_MORE_ITEM',
      payload: true
    })
    page.current = page.current + 1
    fetch()
  }, [dispatch, fetch])
  refs.loadNextPageRef.current = loadNextPage
  return
}

export default useData
